import { AxiosResponse } from 'axios'
import { http } from '../utils/http'
import { PaginatedResponse, ApiCommonParams } from '../types/common.types'
import { NoteProps, HistoryProsp, MedicalRecordsProps } from '../utils/constants'

export interface GetRapportsParams extends ApiCommonParams {
  search?: string;
}

export const getRapports = async (
  params?: GetRapportsParams
): Promise<PaginatedResponse<any> | null> => {
  try {
    const response: AxiosResponse<PaginatedResponse<any>> = await http.get(
      '/api/web/rapport',
      {
        params: {
          offset: params?.offset ?? 0,
          limit: params?.limit ?? 50,
          ...(params?.sort && { sort: params.sort }),
          ...(params?.search && params.search.trim().length > 0 && { search: params.search.trim() }),
        },
      }
    )
    return response.data
  } catch (error) {
    console.error('Error fetching rapports:', error)
    return null
  }
}

const fetchAllPaginatedItems = async <T>(url: string, params: any = {}): Promise<T[]> => {
  const allItems: T[] = []
  let offset = 0
  const limit = 50
  let hasMoreData = true

  while (hasMoreData) {
    const response: AxiosResponse<PaginatedResponse<T>> = await http.get(url, {
      params: {
        ...params,
        offset,
        limit,
      },
    })

    const { items, pagination } = response.data
    allItems.push(...items)

    if (items.length < limit || offset + items.length >= pagination.count) {
      hasMoreData = false
    } else {
      offset += limit
    }
  }

  return allItems
}

export const getNotes = async (id: number): Promise<NoteProps[]> => {
  try {
    return await fetchAllPaginatedItems<NoteProps>(`/api/web/rapport/${id}/note`)
  } catch (error) {
    console.error('Error fetching notes:', error)
    return []
  }
}

export const getHistory = async (id: number): Promise<HistoryProsp[]> => {
  try {
    return await fetchAllPaginatedItems<HistoryProsp>(`/api/web/rapport/${id}/history`)
  } catch (error) {
    console.error('Error fetching history:', error)
    return []
  }
}

export const getAntecedent = async (id: number): Promise<MedicalRecordsProps[]> => {
  try {
    return await fetchAllPaginatedItems<MedicalRecordsProps>(`/api/web/rapport/${id}/antecedent`)
  } catch (error) {
    console.error('Error fetching antecedent:', error)
    return []
  }
}

export const newNote = async (
  id: number,
  note: string,
  isAnticoagulationPatient?: boolean
): Promise<AxiosResponse | any> => {
  try {
    const body: any = { note }
    if (isAnticoagulationPatient) {
      body.isAnticoagulationPatient = true
    }

    const response = await http.post(`/api/web/rapport/${id}/note`, body)

    return response
  } catch (error) {
    return error
  }
}

export const editNote = async (
  rapportId: number,
  noteId: number,
  note: string,
  isAnticoagulationPatient?: boolean
): Promise<AxiosResponse | any> => {
  try {
    const body: any = { note }
    if (isAnticoagulationPatient) {
      body.isAnticoagulationPatient = true
    }

    const response = await http.patch(
      `/api/web/rapport/${rapportId}/note/${noteId}`,
      body
    )

    return response
  } catch (error) {
    return error
  }
}

export const deleteNote = async (
  rapportId: number,
  noteId: number
): Promise<AxiosResponse | any> => {
  try {
    const response = await http.delete(
      `/api/web/rapport/${rapportId}/note/${noteId}`
    )

    return response
  } catch (error) {
    return error
  }
}
